import {
  ActionPopUpSingleButton,
  ContentDetailCard,
  IStyles,
  LockToggleButton,
  SectionListWithTopCarousel,
  deserify,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  resourceTypeName,
  useFeatureEnabled,
} from '@geneo2-web/shared-ui';
import { Box } from '@mui/material';
import { ResourceInfo } from '@protos/content_management/content.common.apis_pb';
import {
  ResourceCategoryEnum,
  Resource_ResourceEnum,
} from '@protos/content_management/content.db_pb';
import { ResourceSelectionFromTypeEnum } from '@protos/geneo_ai/ai.student.events_pb';
import { SessionModeEnum } from '@protos/learning_management/lms.db_pb';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../../reduxStore/reduxHooks';
import { onResourceClick } from '../../../../../utils/learn';
import { interactionEvent } from '../../../../Auth/Login/login_logout.events';
import SectionListSckeleton, {
  ShimmerActiveHwCard,
} from '../../../../Home/shimmer';
import { setResourceRedirectionPath } from '../../../../Learn/reducer/learn.slice';
import { aiResourceOpenEvent } from '../../../../Learn/utils_learn/learn.events';
import { useGlobalContext } from '../../../../../app/Context/GlobalContextProvider';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';
import {
  ContentLockStatusType,
  Feature,
} from '@protos/school_management/school.db_pb';
import { ResourceLock } from '@protos/learning_management/lms.common.apis_pb';

interface IPopupProps {
  iconName: string;
  popupText: string;
  splitText?: string;
  buttontext: string;
  ctaHandler: () => void;
}

interface SuggestedLearningProps {
  resources: ResourceInfo[];
  loading: boolean;
  recommendationId?: string;
  lockedData?: ResourceLock[];
}

const styles: IStyles = {};

export const SuggestedLearning = ({
  resources = [],
  loading = false,
  recommendationId,
  lockedData,
}: SuggestedLearningProps) => {
  const { isOffline } = useDownloadContext();
  const { isIpadOnly } = useGlobalContext();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const pathSegments = location.pathname.split('/');
  const user = deserify(useAppSelector((state) => state.auth.userInfo));
  const [actionPopupState, setActionPopupState] = useState(false);
  const featureInfo = user?.schoolDetails?.featuresPlanInfo;
  const isContentLockFeature = useFeatureEnabled(
    featureInfo,
    Feature.CONTENT_LOCK
  );
  const [popupDetails, setPopupDetails] = useState<IPopupProps>({
    iconName: '',
    buttontext: 'ok',
    popupText: '',
    ctaHandler: () => setActionPopupState(false),
  });

  const showLockPopup = () => {
    setPopupDetails({
      iconName: 'locked@2x',
      buttontext: 'ok',
      popupText:
        'Your school has locked this resource. You will be able to access it once they unlock it',
      ctaHandler: () => setActionPopupState(false),
    });
    setActionPopupState(true);
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: { xs: '100%', lg: pxTovW(555) },
          '&  h2,h3': {
            fontWeight: 'bold',
            fontSize: { xs: pxToRem(18), md: pxTovW(30) },
          },
        }}
      >
        <SectionListSckeleton fullWidth children={ResourceShimmerArray} />
      </Box>
    );
  }
  if (!resources || resources.length === 0) {
    return null;
  }
  return (
    <Box
      sx={{
        width: { xs: '100%', lg: pxTovW(555) },
        '&  h2,h3': {
          fontWeight: 'bold',
          fontSize: { xs: pxToRem(18), md: pxTovW(30) },
          marginBottom: pxToRem(5),
        },
      }}
    >
      <SectionListWithTopCarousel
        containerMdWidth="100%"
        title="Suggested Learning"
        itemsToShow={2}
        items={resources.map((val, index) => (
          <ContentDetailCard
            isLocked={
              isContentLockFeature
                ? getResourceContentLockInfo(val.resourceId, lockedData)
                    ?.lockStatus ===
                  ContentLockStatusType.CONTENT_LOCK_STATUS_IS_LOCKED
                : false
            }
            locked={
              isContentLockFeature && (
                <LockToggleButton
                  isLocked={
                    lockedData
                      ? getResourceContentLockInfo(val.resourceId, lockedData)
                          ?.lockStatus
                      : false
                  }
                />
              )
            }
            onClick={async () => {
              dispatch(setResourceRedirectionPath(location.pathname));
              await aiResourceOpenEvent({
                resourceId: val.resourceId,
                selectedPath: ResourceSelectionFromTypeEnum.PERFOMANCE_HOMEPAGE,
                rank: index + 1,
                recommendationId: recommendationId,
                isOffline: isOffline,
              });
              isContentLockFeature &&
              getResourceContentLockInfo(val.resourceId, lockedData)
                ?.lockStatus ===
                ContentLockStatusType.CONTENT_LOCK_STATUS_IS_LOCKED
                ? showLockPopup()
                : onResourceClick(
                    navigate,
                    {
                      resourceId: val.resourceId,
                    },
                    SessionModeEnum.SESSION_MODE_RESOURCE
                  );
              await interactionEvent({
                url: '',
                context: 'suggested_learnings',
                name: 'SUGGESTED_LEARNINGS_OPEN',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            key={'res_' + index}
            variant={isIpadOnly ? 'medium' : 'small'}
            image={getMediaBasePath(
              val.posterImageUrl,
              val.resourceCategoryType ===
                ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
                ? 'userDataBucket'
                : 'processedMediaBucket'
            )}
            heading={val.title}
            iconDetails={[
              { iconName: 'clock', text: `${val.estimatedTimeInMin} Min` },
              {
                iconName: resourceTypeName(val.resourceType)?.icon,
                text: resourceTypeName(val.resourceType)?.name,
              },
            ]}
            showVideoIcon={
              val.resourceType === Resource_ResourceEnum.RESOURCE_TYPE_VIDEO
            }
            rootStyle={{
              backgroundColor: 'common.white',
            }}
          />
        ))}
      />
      <ActionPopUpSingleButton
        fontSmall={true}
        open={actionPopupState}
        iconName={popupDetails.iconName}
        popupText={popupDetails.popupText}
        handleClose={() => setActionPopupState(false)}
        splitText={popupDetails.splitText}
        ctaHandler={popupDetails.ctaHandler}
        buttontext="OK"
        background="rgba(193, 248, 218, 1)"
      />
    </Box>
  );
};

const ResourceShimmerArray = [
  <ShimmerActiveHwCard variant="small" key={1} />,
  <ShimmerActiveHwCard variant="small" key={2} />,
];
const getResourceContentLockInfo = (
  resourceId: string,
  contentLockData?: ResourceLock[]
) => {
  return contentLockData?.find((val) => val.resourceId === resourceId);
};
